import POIFieldsList, { POIHeaderMapping } from "@/constants/poiFields";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldModal } from "@emerald-react/modal";
import { EmeraldNotification } from "@emerald-react/notification";
import { EmeraldSelect } from "@emerald-react/select";
import { EmeraldStepperV2 } from "@emerald-react/stepper-v2";
import { useCallback, useEffect, useMemo, useState } from "react";

interface POIColumnMatchingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (mappings: Record<string, string>) => void;
  importResult: {
    matched: string[];
    notMatched: string[];
    unParsedColumns: string[][];
  } | null;
  isProcessing?: boolean;
}

const isBlankColumn = (columnName: string): boolean =>
  !columnName ||
  columnName.trim() === "" ||
  columnName.toLowerCase() === "column" ||
  /^column\s*\d*$/i.test(columnName.trim());

const ColumnNameDisplay = ({ columnName }: { columnName: string }) => {
  if (isBlankColumn(columnName)) {
    return (
      <div className="flex items-center gap-2">
        <EmeraldIcon
          variant="icon"
          icon="warning"
          type="filled"
          fontsizeInRem={1.5}
          className="text-error-icon"
        />
        <span className="text-sm text-error-icon font-medium">
          Column was blank
        </span>
      </div>
    );
  }
  return <div className="text-sm text-data">{columnName}</div>;
};

const SearchableSelect = ({
  column,
  value,
  options,
  onChange,
}: {
  column: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (column: string, value: string) => void;
}) => {
  const handleChange = (_: unknown, selectedValue: string | string[]) => {
    if (typeof selectedValue === "string") {
      onChange(column, selectedValue === "" ? "DONT_IMPORT" : selectedValue);
    }
  };

  return (
    <EmeraldSelect
      clearable
      label=""
      placeholder="Search..."
      value={value || ""}
      options={options}
      onChange={handleChange}
      searchable
      size="small"
    />
  );
};

const POIColumnMatchingModal = ({
  isOpen,
  onClose,
  onNext,
  importResult,
  isProcessing = false,
}: POIColumnMatchingModalProps) => {
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>(
    {},
  );
  const [clearedColumns, setClearedColumns] = useState<Set<string>>(new Set());
  const [originalMatchedColumns, setOriginalMatchedColumns] = useState<
    string[]
  >([]);
  const [originalUnmatchedColumns, setOriginalUnmatchedColumns] = useState<
    string[]
  >([]);

  const allTargetColumns = useMemo(() => {
    const base = POIFieldsList.map((f) => ({
      value: f.key,
      label: f.label,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return [
      { value: "search..", label: "Search..." },
      { value: "DONT_IMPORT", label: "Don't import" },
      ...base,
    ];
  }, []);

  const findMatchedField = useCallback((columnName: string): string => {
    if (isBlankColumn(columnName)) return "DONT_IMPORT";

    const lower = columnName.toLowerCase();
    const normalise = (s: string) =>
      s
        .replace(/[()]/g, "")
        .replace(/-/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    const lowerNorm = normalise(lower);

    // Check the explicit header mapping first (exact)
    const exactKey = Object.entries(POIHeaderMapping).find(
      ([header]) => header.toLowerCase() === lower,
    );
    if (exactKey) return exactKey[1];

    // Check header mapping with normalised comparison
    const normKey = Object.entries(POIHeaderMapping).find(
      ([header]) => normalise(header.toLowerCase()) === lowerNorm,
    );
    if (normKey) return normKey[1];

    // Partial match against field labels/keys (normalised)
    const partialMatch = POIFieldsList.find((f) => {
      const labelNorm = normalise(f.label.toLowerCase());
      const keyNorm = normalise(f.key.toLowerCase());
      return (
        labelNorm.includes(lowerNorm) ||
        lowerNorm.includes(labelNorm) ||
        keyNorm.includes(lowerNorm) ||
        lowerNorm.includes(keyNorm)
      );
    });
    if (partialMatch) return partialMatch.key;

    return "DONT_IMPORT";
  }, []);

  const getColumnValue = useCallback(
    (column: string): string => {
      if (columnMappings[column]) return columnMappings[column];
      if (clearedColumns.has(column)) return "DONT_IMPORT";
      return findMatchedField(column);
    },
    [columnMappings, clearedColumns, findMatchedField],
  );

  const isExcludedColumn = useCallback((columnName: string) => {
    const lower = columnName.toLowerCase();
    return ["flyer", "siteplan", "site plan", "site_plan", "photos", "photo"].some(
      (excluded) => lower.includes(excluded),
    );
  }, []);

  const filteredColumnCategories = useMemo(
    () => ({
      matched: originalMatchedColumns.filter(
        (c) => !isBlankColumn(c) && !isExcludedColumn(c),
      ),
      notMatched: originalUnmatchedColumns.filter(
        (c) => !isExcludedColumn(c),
      ),
    }),
    [originalMatchedColumns, originalUnmatchedColumns, isExcludedColumn],
  );

  // Required fields: categoryName + addressLine1 + city + state, OR latitude + longitude
  const isNextEnabled = useMemo(() => {
    if (!importResult) return false;
    const allCols = [...importResult.matched, ...importResult.notMatched];
    const isMapped = (key: string) =>
      Object.values(columnMappings).includes(key) ||
      allCols.some((c) => getColumnValue(c) === key);

    const addressMapped =
      isMapped("categoryName") &&
      isMapped("addressLine1") &&
      isMapped("city") &&
      isMapped("state");
    const latLngMapped = isMapped("latitude") && isMapped("longitude");
    return addressMapped || latLngMapped;
  }, [importResult, columnMappings, getColumnValue]);

  const handleColumnMapping = useCallback(
    (sourceColumn: string, targetColumn: string) => {
      if (targetColumn !== "DONT_IMPORT") {
        setClearedColumns((prev) => {
          const next = new Set(prev);
          next.delete(sourceColumn);
          return next;
        });
      }
      setColumnMappings((prev) => {
        const next = { ...prev };
        if (targetColumn !== "DONT_IMPORT") {
          // clear any previous mapping to the same target
          Object.entries(next).forEach(([col, val]) => {
            if (col !== sourceColumn && val === targetColumn) {
              next[col] = "DONT_IMPORT";
            }
          });
        }
        next[sourceColumn] = targetColumn;
        return next;
      });
    },
    [],
  );

  const handleNext = useCallback(() => {
    const allCols = [
      ...filteredColumnCategories.matched,
      ...filteredColumnCategories.notMatched,
    ];
    const processedMappings = Object.fromEntries(
      allCols.map((header) => {
        const value = getColumnValue(header);
        return [
          header,
          value === "DONT_IMPORT" ? "NO_MATCH" : value || "NO_MATCH",
        ];
      }),
    );
    onNext(processedMappings);
  }, [filteredColumnCategories, getColumnValue, onNext]);

  const handleCancel = useCallback(() => {
    setColumnMappings({});
    setClearedColumns(new Set());
    onClose();
  }, [onClose]);

  // Reset state when new import result arrives
  useEffect(() => {
    if (!importResult) return;

    setClearedColumns(new Set());
    setColumnMappings({});

    const matched: string[] = [];
    const notMatched: string[] = [];
    const normalise = (s: string) =>
      s
        .replace(/[()]/g, "")
        .replace(/-/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

    [...importResult.matched, ...importResult.notMatched].forEach((col) => {
      const lower = col?.toLowerCase() ?? "";
      if (!col || /^column\s*\d*$/i.test(lower)) {
        notMatched.push(col);
        return;
      }
      const colNorm = normalise(col);
      const hasMatch =
        Object.keys(POIHeaderMapping).some(
          (h) => normalise(h) === colNorm,
        ) ||
        POIFieldsList.some(
          (f) =>
            normalise(f.label) === colNorm ||
            f.key.toLowerCase() === lower ||
            normalise(f.label).includes(colNorm) ||
            colNorm.includes(normalise(f.label)),
        );
      if (hasMatch) {
        matched.push(col);
      } else {
        notMatched.push(col);
      }
    });

    setOriginalMatchedColumns(matched);
    setOriginalUnmatchedColumns(notMatched);
  }, [importResult?.matched, importResult?.notMatched]);

  if (!importResult) return null;

  const allColumns = [...importResult.matched, ...importResult.notMatched];
  const blankHeaderCount = allColumns.filter(isBlankColumn).length;

  if (blankHeaderCount >= 2) {
    return (
      <EmeraldModal
        opened={isOpen}
        title="Import Error"
        onClose={handleCancel}
        headerText="Import Error"
        hasCloseIcon={true}
        className="column-matching-modal"
        actionTemplate={
          <EmeraldButton
            label="Close"
            type="Primary"
            onClick={handleCancel}
            className="mdc-dialog__button"
            color="primary"
          />
        }
      >
        <div className="flex flex-col max-h-[80vh]">
          <div className="px-6 py-4 column-matching-modal-notifications">
            <EmeraldNotification
              variant="error"
              closable={false}
              title="Multiple Blank Column Headers Detected"
              message="The file you are trying to import has more than one empty/blank column headers. Please revisit the file."
            />
          </div>
        </div>
      </EmeraldModal>
    );
  }

  return (
    <EmeraldModal
      opened={isOpen}
      title="Import POIs from Excel"
      onClose={handleCancel}
      headerText="Import POIs from Excel"
      hasCloseIcon={true}
      className="column-matching-modal"
      actionTemplate={
        <>
          <EmeraldButton
            label="Cancel"
            type="Secondary"
            onClick={handleCancel}
            disabled={isProcessing}
            className="mdc-dialog__button"
            color="primary"
          />
          <EmeraldButton
            label={isProcessing ? "Processing..." : "Next"}
            type="Primary"
            onClick={handleNext}
            disabled={isProcessing || !isNextEnabled}
            className="mdc-dialog__button"
            color="primary"
          />
        </>
      }
    >
      <div className="flex flex-col max-h-[80vh]">
        <div className="px-6">
          <EmeraldStepperV2
            onClick={function Ga() {}}
            orientation="horizontal"
            steps={[
              { id: "1", label: "Match Columns", state: "current" },
              { id: "2", label: "Row Report", state: "upcoming" },
            ]}
          />
          <p className="text-sm text-data">
            Must map: Point of Interest Group Name, Address, City, and State.
            Alternatively, map Latitude and Longitude. Each field can only be
            mapped once.
          </p>
        </div>

        <div className="flex-1 p-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Matched Columns */}
            <div>
              <div className="mb-4 column-matching-modal-notifications">
                <EmeraldNotification
                  variant="success"
                  closable={false}
                  title={`${filteredColumnCategories.matched.length} Columns matched`}
                  message={`We matched ${filteredColumnCategories.matched.length} of ${filteredColumnCategories.matched.length + filteredColumnCategories.notMatched.length} columns.`}
                />
              </div>
              <div className="flex flex-col gap-3">
                {filteredColumnCategories.matched.map((column) => (
                  <div
                    key={column}
                    className="grid grid-cols-2 items-center gap-2"
                  >
                    <ColumnNameDisplay columnName={column} />
                    <SearchableSelect
                      column={column}
                      value={getColumnValue(column)}
                      options={allTargetColumns}
                      onChange={handleColumnMapping}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Unmatched Columns */}
            <div>
              <div className="mb-4 column-matching-modal-notifications">
                <EmeraldNotification
                  variant="warning"
                  closable={false}
                  title={`${filteredColumnCategories.notMatched.length} Columns unmatched`}
                  message="Review and manually map these columns, or leave them as Don't import."
                />
              </div>
              <div className="flex flex-col gap-3">
                {filteredColumnCategories.notMatched.map((column) => (
                  <div
                    key={column}
                    className="grid grid-cols-2 items-center gap-2"
                  >
                    <ColumnNameDisplay columnName={column} />
                    <SearchableSelect
                      column={column}
                      value={getColumnValue(column)}
                      options={allTargetColumns}
                      onChange={handleColumnMapping}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </EmeraldModal>
  );
};

export default POIColumnMatchingModal;
